/**
 * DataTableFacetedFilter Component
 *
 * Multi-select dropdown filter for DataTable with keyboard shortcuts.
 * Wraps the core MultiSelectDropdown component and adds DataTable-specific
 * shortcut integration (e.g., pressing "3" to open the 3rd filter) and the
 * standardised corner Counter used across the app's filter triggers.
 */

// React
import { useState, useEffect, useRef, useCallback } from 'react';

// UI primitives
import { Counter } from '@/components/ui/counter';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';

// Shared components
import { useDataTableShortcuts } from './DataTableShortcuts';

// Hooks and utilities
import { playTickSound } from '../../utils/sounds';

// Types
import type { FilterOption, DataTableFacetedFilterProps } from './types';
import { FILTER_SURFACE } from '@/components/ui/field-surface';
import { cn } from '@/lib/utils';

export type { FilterOption, DataTableFacetedFilterProps };

export function DataTableFacetedFilter({
  title,
  options,
  selected,
  onSelectedChange,
  multiple = true,
  shortcutIndex,
  className,
}: DataTableFacetedFilterProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const shortcuts = useDataTableShortcuts();

  // Register shortcut to open this filter
  useEffect(() => {
    if (!shortcuts || shortcutIndex === undefined) return;

    return shortcuts.register(
      shortcutIndex,
      () => {
        setOpen((prev) => !prev);
        triggerRef.current?.focus();
      },
      'filter'
    );
  }, [shortcuts, shortcutIndex]);

  // Wrap onSelectedChange to add sound effect
  const handleSelectedChange = useCallback(
    (newSelected: string[]) => {
      // Play tick sound based on selection change
      const wasAdding = newSelected.length > selected.length;
      playTickSound(wasAdding);
      onSelectedChange(newSelected);
    },
    [selected.length, onSelectedChange]
  );

  const selectedCount = selected.length;

  // Convert FilterOption to SelectOption format
  const selectOptions = options.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));

  return (
    <div className="relative">
      <MultiSelectDropdown
        title={title}
        options={selectOptions}
        selected={selected}
        onSelectedChange={handleSelectedChange}
        multiple={multiple}
        open={open}
        onOpenChange={setOpen}
        triggerRef={triggerRef}
        className={cn(FILTER_SURFACE, className)}
      />
      {selectedCount > 0 && (
        <Counter
          value={selectedCount}
          className="absolute -top-1.5 -right-1.5"
          aria-label={`${selectedCount} ${title} selected`}
        />
      )}
    </div>
  );
}
